import { formatDuration, formatKm } from '@shared/format';
import type { Dataset, Route } from '@shared/model/dataset';
import styles from './TrailsTable.module.css';

interface Props {
    dataset: Dataset;
    selectedRouteId: string | null;
    onSelect: (route: Route) => void;
}

function status(route: Route) {
    if (route.closed) {
        let title = 'Sentier fermé';
        if (route.cause) title += ` — ${route.cause}`;
        if (route.reopenDate) title += ` — Réouverture : ${route.reopenDate}`;
        return <abbr title={title}>⛔️ Non</abbr>;
    }
    if (route.onfManaged) return <>✅ Oui</>;
    return <abbr title="Chemin non géré par l'ONF">n/a</abbr>;
}

/** Every trail grouped by coarse location, as on /sentiers/. */
export function TrailsTable({ dataset, selectedRouteId, onSelect }: Props) {
    const groups = dataset.routesByArea();
    return (
        <table id="table-sentiers" className={styles.table}>
            {[...groups].map(([area, routes]) => (
                <tbody key={area}>
                    <tr className={styles.areaRow}>
                        <th scope="colgroup" className={styles.area}>
                            {area}
                        </th>
                        <th scope="col">Distance</th>
                        <th scope="col">Dénivelé (+/-)</th>
                        <th scope="col">Durée (aller/retour)</th>
                        <th scope="col">Ouvert</th>
                    </tr>
                    {routes.map((route) => (
                        <tr
                            key={route.id}
                            className={`${styles.row} ${route.id === selectedRouteId ? styles.selected : ''}`}
                            onClick={() => onSelect(route)}
                            data-route-id={route.id}
                        >
                            <td>
                                {route.from.name} → {route.to.name}
                                {route.info && (
                                    <>
                                        <br />
                                        <small>{route.info}</small>
                                    </>
                                )}
                            </td>
                            <td className={styles.nowrap}>{formatKm(route.distance)}</td>
                            <td className={styles.nowrap}>
                                <span className={styles.num}>{route.data.up} m</span>
                                <span className={styles.sep}>/</span>
                                <span className={styles.num}>{route.data.down} m</span>
                            </td>
                            <td className={styles.nowrap}>
                                <span className={styles.num}>{formatDuration(route.forward.duration)}</span>
                                <span className={styles.sep}>/</span>
                                <span className={styles.num}>{formatDuration(route.backward.duration)}</span>
                            </td>
                            <td className={styles.center}>{status(route)}</td>
                        </tr>
                    ))}
                </tbody>
            ))}
        </table>
    );
}
